import { RoomSchema } from '../../../../definitions/schemas';
import ContentType from '../../../../enums/ContentType';

const composeKey = (
  contactId: string,
  welcomeMessageId: string,
  version: number,
  useContactAsEntity = false
) => {
  let versionKey = '';
  // compatibility with existing logic for WelcomeMessage viewed for version 1
  // only add version string for new versions
  // if there are a new version, we should display the overlay again
  if (version > 1) {
    versionKey = `-version-${version}`;
  }

  // @todo this is to ensure retrocompatibility with old keys, must be removed
  // once internal rooms are generally available
  const entity = useContactAsEntity ? 'contact' : 'visitor';
  return `welcome-message-${welcomeMessageId}-${entity}-${contactId}${versionKey}`;
};

/**
 * Checks if a room's welcome message should be displayed as an overlay
 * to the visitor. This is only valid for audio, text and video
 * welcome messages. Also, if the visitor has already viewed the message,
 * there will be a flag stored in local storage, so we also check that
 * to avoid displaying the overlay twice.
 */
export const shouldDisplayWelcomeMessageOverlay = (
  visitorId: string,
  welcomeMessage: RoomSchema['welcomeMessage']
): boolean => {
  if (!welcomeMessage?.id) {
    return false;
  }

  const typesSupportingOverlay = [
    ContentType.AUDIO,
    ContentType.TEXT,
    ContentType.VIDEO,
  ];

  const isSupportedType =
    !!welcomeMessage?.type &&
    typesSupportingOverlay.includes(welcomeMessage.type as ContentType);

  if (!isSupportedType) {
    return false;
  }

  const { id, version } = welcomeMessage;

  let localStorageKey = composeKey(visitorId, id, version!);
  let item = localStorage.getItem(localStorageKey);

  if (!item) {
    // a second attempt tries to retrieve the item using the contact entity
    // as part of the key, to ensure retrocompatibility with new visitor approach
    localStorageKey = composeKey(visitorId, id, version!, true);
    item = localStorage.getItem(localStorageKey);
  }

  return !item;
};

/**
 * Stores a local storage key with a relationship between the contact that
 * viewed the welcome message and the welcome message that was viewed. This
 * avoids showing the welcome as an overlay in subsequent visits to the room
 * by the same contact, once they have seen it in the same device.
 */
export const markWelcomeMessageAsViewed = (
  visitorId: string,
  welcomeMessage: RoomSchema['welcomeMessage']
) => {
  if (!welcomeMessage?.id) {
    return;
  }

  const { id, version } = welcomeMessage;
  const localStorageKey = composeKey(visitorId, id, version!);
  localStorage.setItem(localStorageKey, '1');
};

/**
 * Removing the welcome message viewed local storage key, for scenarios where
 * an anonymous contact identifies by entering an email, which could potentially
 * end up with a different contact id
 */
export const removeObsoleteWelcomeMessageViewed = (
  visitorId: string,
  welcomeMessage: RoomSchema['welcomeMessage']
) => {
  if (!welcomeMessage?.id) {
    return;
  }

  const localStorageKey = composeKey(
    visitorId,
    welcomeMessage.id,
    welcomeMessage.version!
  );

  localStorage.removeItem(localStorageKey);
};
